import React, { useMemo, useCallback, useState } from 'react';
import { Text as RNText, View } from 'react-native';
import Animated from 'react-native-reanimated';
import getListAccounts from 'utils/api/getListAccounts';
import getListOfflineAccounts from 'utils/api/getListOfflineAccounts';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { selectActiveConnections, selectDefaultCurrency, } from 'reducers/selectors';
import { setDeviceSettingsKey } from 'actions/deviceSettings';
import { AppView, rem, SCROLL_INDICATOR_INSETS, StatusBar, withAnimatedNavigationHeader, } from 'design-system';
import Text from 'design-system/Text';
import createStyleSheets from 'utils/createStyleSheets';
import useStyles from 'hooks/useStyles';
import { useMarginBottom } from 'hooks/useMarginBottom';
import Button, { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import LogoBankAccount from 'design-system/LogoBankAccount';
import { formatAmount, getOrdinalSuffix } from 'utils';
import { createFilter, createSmartRule } from 'actions';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import useRequiredBenefitCallback from 'features/premium/hooks/useRequiredBenefitCallback';
import { SmartRuleIconForTransactionType } from '../components/SmartRuleIconForTransactionType';
const strings = {
    title: 'Review a smart rule',
};
const transactionTypeLabels = {
    both: 'to / from',
    income: 'to',
    spending: 'from',
};
const SmartRuleReviewScreen = withAnimatedNavigationHeader(({ navigation, route, scrollHandler }) => {
    const { name, accountIds, type, counterpartRegex, maxAmount, minAmount, minDayOfMonth, maxDayOfMonth, targetAccountId, } = route.params;
    const dispatch = useAppDispatch();
    const [isFetching, setIsFetching] = useState(false);
    const activeConnections = useAppSelector(selectActiveConnections);
    const detectingAccounts = useMemo(() => getListAccounts(activeConnections).filter((acc) => accountIds.includes(acc.id)), [accountIds, activeConnections]);
    const targetAccount = useMemo(() => getListOfflineAccounts(activeConnections).find((acc) => acc.id === targetAccountId), [activeConnections, targetAccountId]);
    const didViewSmartRulesIntro = useAppSelector((store) => store.deviceSettings.didViewSmartRulesIntro);
    const onSave = useRequiredBenefitCallback(useCallback(async () => {
        setIsFetching(true);
        const body = { criteria: {} };
        body.criteria.accountIds = accountIds;
        body.criteria.counterpartRegex = counterpartRegex;
        body.criteria.type = type;
        body.criteria.minDayOfMonth = minDayOfMonth;
        body.criteria.maxDayOfMonth = maxDayOfMonth;
        if (minAmount)
            body.criteria.minAmount = minAmount;
        if (maxAmount)
            body.criteria.maxAmount = maxAmount;
        const response = await dispatch(createFilter(body));
        if (response?.type !== 'CREATE_FILTER_SUCCESS') {
            setIsFetching(false);
            return;
        }
        const res = await dispatch(createSmartRule({
            filterId: response.payload.filter.id,
            targetAccountId,
            ruleName: name,
        }));
        const hasSetUpEmmaSelectAssetOrDebtRoute = !!navigation
            .getState()
            .routes.find((route) => route.name === 'SetUpEmmaSelectAssetOrDebt');
        if (res?.type !== 'CREATE_SMART_RULE_SUCCESS') {
            setIsFetching(false);
            return;
        }
        navigation.navigateDeprecated('Success', {
            title: 'Smart rule has been created!',
            subTitle: '',
            onContinue: () => {
                if (didViewSmartRulesIntro) {
                    navigation.pop(6);
                }
                else {
                    navigation.pop(7);
                }
                dispatch(setDeviceSettingsKey('didViewSmartRulesIntro', true));
                if (hasSetUpEmmaSelectAssetOrDebtRoute) {
                    navigation.navigate('SetUpEmmaAddAnotherBank', {
                        isFromAddAssetOrDebtFlow: true,
                    });
                }
                else {
                    navigation.navigateDeprecated('SmartRules');
                }
                return true;
            },
        });
        setIsFetching(false);
    }, [
        accountIds,
        counterpartRegex,
        didViewSmartRulesIntro,
        maxAmount,
        maxDayOfMonth,
        minAmount,
        minDayOfMonth,
        name,
        targetAccountId,
        type,
    ]), 'smartRules');
    const currency = useAppSelector(selectDefaultCurrency);
    const styles = useStyles(styleSet);
    const { paddingHorizontalStyle } = useAppFrameDimensions();
    const contentContainerStyle = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, styles.container, paddingHorizontalStyle);
    if (!targetAccount)
        return null;
    const [periodPrefix, period] = useMemo(() => {
        if (!minDayOfMonth && !maxDayOfMonth)
            return [];
        if (minDayOfMonth && !maxDayOfMonth) {
            return [
                'from',
                `${minDayOfMonth}${getOrdinalSuffix(minDayOfMonth)} of the month`,
            ];
        }
        if (!minDayOfMonth && maxDayOfMonth) {
            return [
                'until',
                `${maxDayOfMonth}${getOrdinalSuffix(maxDayOfMonth)} of the month`,
            ];
        }
        if (minDayOfMonth === 1 && maxDayOfMonth === 31) {
            return ['on', 'any day of the month'];
        }
        return [
            'during',
            `${minDayOfMonth}${getOrdinalSuffix(minDayOfMonth)} to ${maxDayOfMonth}${getOrdinalSuffix(maxDayOfMonth)} of the month`,
        ];
    }, [maxDayOfMonth, minDayOfMonth]);
    const [valuePrefix, value1, value2] = useMemo(() => {
        if (!minAmount && !maxAmount)
            return [];
        if (minAmount && !maxAmount) {
            return ['and values from', formatAmount(minAmount, currency)];
        }
        if (!minAmount && maxAmount) {
            return ['and values up to', formatAmount(maxAmount, currency)];
        }
        return [
            'and values from',
            formatAmount(minAmount, currency),
            formatAmount(maxAmount, currency),
        ];
    }, [currency, maxAmount, minAmount]);
    return (<AppView withFrame={false}>
        <StatusBar barStyle="dark-content" animated/>
        <Animated.ScrollView onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS} contentContainerStyle={contentContainerStyle}>
          <Text Text-16 Secondary>
            {strings.title}
          </Text>
          <Text Text-24 style={styles.title}>
            {name}
          </Text>
          <View style={styles.innerContainer}>
            <Text TextThin-16 Secondary>
              Every transaction with reference
            </Text>
            <Text Text-16 style={styles.pill}>
              {counterpartRegex}
            </Text>
            {valuePrefix && value1 ? (<>
                <Text TextThin-16 Secondary>
                  {valuePrefix}
                </Text>
                <Text Text-16 style={styles.pill}>
                  {value1}
                </Text>
                {value2 ? (<>
                    <Text TextThin-16 Secondary>
                      to
                    </Text>
                    <Text Text-16 style={styles.pill}>
                      {value2}
                    </Text>
                  </>) : null}
              </>) : null}
            {periodPrefix && period ? (<>
                <Text TextThin-16 Secondary>
                  {periodPrefix}
                </Text>
                <Text Text-16 style={styles.pill}>
                  {period}
                </Text>
              </>) : null}
            <Text TextThin-16 Secondary>
              that comes
            </Text>
            <View style={styles.pill}>
              <SmartRuleIconForTransactionType type={type} width={20} height={20}/>
              <Text Text-16>{transactionTypeLabels[type]}</Text>
            </View>
            {detectingAccounts.map((acc) => (<View key={acc.id} style={styles.pill}>
                <View style={styles.bankLogo}>
                  <LogoBankAccount size="R44" data={acc} showHidden/>
                </View>
                <Text Text-16>{acc.name}</Text>
              </View>))}
            <Text TextThin-16 Secondary>
              will create transactions in
            </Text>
            <View style={styles.pill}>
              {'emoji' in targetAccount && targetAccount.emoji ? (<RNText style={styles.emoji}>{targetAccount.emoji}</RNText>) : (<View style={styles.bankLogo}>
                  <LogoBankAccount size="R44" data={targetAccount} showHidden/>
                </View>)}
              <Text Text-16>{targetAccount.name}</Text>
            </View>
          </View>
        </Animated.ScrollView>
        <Button floating brand title="Save" onPress={onSave} isFetching={isFetching}/>
      </AppView>);
}, ({ colors }) => ({
    title: strings.title,
    hideTitleOnScroll: true,
    headerColorOutputRange: [
        colors.background.dark,
        colors.cards.onDark,
    ],
}));
const styleSet = createStyleSheets((colors) => ({
    container: {
        marginHorizontal: rem(16),
    },
    innerContainer: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'center',
        columnGap: rem(8),
        rowGap: rem(12),
    },
    title: {
        marginTop: rem(4),
        marginBottom: rem(32),
    },
    bankLogo: {
        justifyContent: 'center',
        alignItems: 'center',
        transform: [{ scale: 0.55 }],
        width: 24,
        height: 24,
    },
    emoji: {
        color: 'black',
        fontSize: 18,
        width: 24,
        fontFamily: 'System',
        textAlign: 'center',
    },
    pill: {
        flexDirection: 'row',
        alignItems: 'center',
        gap: rem(8),
        borderRadius: rem(12),
        overflow: 'hidden',
        paddingVertical: rem(8),
        paddingHorizontal: rem(12),
        backgroundColor: colors.cards.onDark,
    },
}));
export default SmartRuleReviewScreen;
